import type { CreditTaxLine } from '../api/billing';
import { describeTax } from '../shared-ui/taxLine';

// ════════════════════════════════════════════════════════════════════════════
// PortalTaxLine — the Stripe-computed tax of one purchase, as a small dim line under the row
// (Stripe Tax adoption Phase 3, voice-assistant issue #2176). A pure renderer of the backend's
// `tax` view (read from `stripe_tax_record`); the phrasing lives in shared-ui/taxLine.ts.
//
// Renders NOTHING when there is nothing to say — a legacy purchase (no recorded tax fact), a bonus/burn row,
// an older backend that sends no `tax` at all: never a "0", never a guess. No wallet FSM, no store — it is
// read-only display over the same fetch state PortalHistoryTab / PortalReceiptsTab already own.
// Pinned by setpoint-evals/stripe-tax-p3/SE-02.
// ════════════════════════════════════════════════════════════════════════════

interface Props {
  tax?: CreditTaxLine | null;
  /** Fiscal EUR gross ("40.00") — History rows lead with it ("Paid EUR 40.00 · …"); Receipts already show it as the headline. */
  paidEur?: string | null;
  /** Receipts already render the country in their meta row — they pass false to avoid saying it twice. */
  showCountry?: boolean;
}

export function PortalTaxLine({ tax, paidEur, showCountry = true }: Props) {
  const t = describeTax(tax);
  if (!t) return null;
  const gross = paidEur != null && Number.isFinite(Number(paidEur)) ? `Paid EUR ${Number(paidEur).toFixed(2)}` : null;
  const parts = [gross, t.vat, showCountry ? t.country : null].filter((p): p is string => !!p);
  // `paid` alone is not tax information: with no VAT part and no country to show there is no line.
  if (!t.vat && !(showCountry && t.country)) return null;
  return (
    <div
      class="portal-tax-line"
      data-testid="portal-tax-line"
      style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)', marginTop: 2, lineHeight: 1.4 }}
    >
      {parts.join(' · ')}
    </div>
  );
}
